import styled from '@/styles';
import { DependabilityEvent } from '@/services/dependabilityV2API';
import { elapsedLabel, exactTime } from '@/util/relativeTime';
import { dependabilityV2Colors } from '@/styles/colors';
import { feedAccentColor } from './tierColors';
import { FlagSolidIcon } from './icons';

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  variants: {
    // the in-card section carries its own 16px bottom padding in the frames
    // (rows already pad 16 below, so the card shows 32px under the last row)
    section: { true: { paddingBottom: '16px' } },
  },
});
const HeaderRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '8px',
});
const SectionTitle = styled('div', {
  fontWeight: 700,
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
});
const List = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});
// Row template EL-6c7ee9a4: every row pads 16px below, including the last
const Entry = styled('div', {
  paddingBottom: '16px',
});
const DescriptionRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});
const AccentBar = styled('span', {
  flex: 'none',
  width: '3px',
  height: '18px',
  borderRadius: '100px',
});
const Description = styled('div', {
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
});
const MetaRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '$md',
  lineHeight: 1.4,
  color: dependabilityV2Colors.depMetaText,
});
const MetaDot = styled('span', {
  flex: 'none',
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  backgroundColor: dependabilityV2Colors.depMetaText,
});
// GRAVY-10365 — the "Flagged" pill beside a location-flag headline.
//
// UNCONDITIONAL for a location_mismatched entry, with no status variants: product removed
// "Under review", "For review" and "Approved" from this page (D6), so the pill's only job
// is to mark that the event WAS flagged. The feed is a historical log of immutable ledger
// rows, so an entry stays "Flagged" even after ops resolves it — the current status lives
// in the Location Flag modal, which is the surface that can change.
const FlaggedPill = styled('span', {
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '0 8px',
  borderRadius: '8px',
  fontSize: '$sm',
  fontWeight: 700,
  color: dependabilityV2Colors.depTierMonitored,
});
const EmptyNote = styled('div', {
  fontSize: '$md',
  color: dependabilityV2Colors.depMetaText,
});
const ViewMore = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$md',
  alignSelf: 'flex-start',
});

interface Props {
  events: DependabilityEvent[];
  hasMore: boolean;
  loading: boolean;
  onViewMore: () => void;
  // The card feed shows the "Recent activity" header with View more top-right
  // (view-activities frame); inside the activity modal the title bar owns the
  // heading, so the body renders rows only, with pagination at the bottom.
  withHeader?: boolean;
  // A swallowed fetch failure must surface as a retryable note rather than the
  // false "No score activity yet" empty state.
  error?: boolean;
  onRetry?: () => void;
}

// snake_case ledger enum -> sentence case ("score_initialized" -> "Score
// initialized"). Deliberately generic rather than a lookup table: the backend
// appends new event types as the scoring rules grow, and an unmapped type must
// still read as words, not fall back to the raw identifier.
/**
 * The event type the feed projects a location flag as (GRAVY-10365).
 *
 * Flags are no longer written into the dependability ledger — the events
 * endpoint MERGES the two tables and tags each row with `source`. Prefer
 * `isLocationFlag`, which keys on the source; this constant remains because the
 * type is still what the row is labelled with.
 */
export const LOCATION_FLAG_EVENT_TYPE = 'location_flagged';

/**
 * The ledger and the flags have INDEPENDENT id spaces, so a row is identified
 * by source + id — never id alone.
 */
export const isLocationFlag = (event: DependabilityEvent): boolean =>
  event.source === 'location_flag';

export const feedKey = (event: DependabilityEvent): string =>
  `${event.source ?? 'dependability'}-${event.id}`;

export const humanizeEventType = (eventType: string): string => {
  const words = (eventType ?? '').replace(/_/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const ActivityFeed = ({
  events,
  hasMore,
  loading,
  onViewMore,
  withHeader = true,
  error = false,
  onRetry,
}: Props) => {
  const viewMore = hasMore && (
    <ViewMore type="button" onClick={onViewMore} disabled={loading}>
      View more
    </ViewMore>
  );

  const isEmpty = events.length === 0 && !loading;

  // Rendered before the list when nothing loaded, and AFTER the list when a
  // page-N "View more" fails (fix round 3): with prior events kept, the old
  // `isEmpty && error` gate made a failed page-2 fetch a completely silent
  // no-op — no message, no growth, no way to tell it from "no more pages".
  const errorNote = error && (
    <EmptyNote>
      Couldn&apos;t load activity.{' '}
      {onRetry && (
        <ViewMore type="button" onClick={onRetry}>
          Try again
        </ViewMore>
      )}
    </EmptyNote>
  );

  return (
    <Wrapper section={withHeader}>
      {withHeader && (
        <HeaderRow>
          <SectionTitle>Recent activity</SectionTitle>
          {viewMore}
        </HeaderRow>
      )}
      {isEmpty && errorNote}
      {isEmpty && !error && <EmptyNote>No score activity yet</EmptyNote>}
      <List>
        {events.map((event) => (
          <Entry key={feedKey(event)} data-testid={`feed-entry-${event.id}`}>
            <DescriptionRow>
              <AccentBar
                data-testid={`feed-accent-${event.id}`}
                style={{ backgroundColor: feedAccentColor(event.category) }}
              />
              <Description>{event.description}</Description>
              {/* Keyed off `source`, not the type string: the row comes from a
                  different table, and the merge is what the backend guarantees.
                  After D6 the pill has no status variants, so presence alone
                  decides it. */}
              {isLocationFlag(event) && (
                <FlaggedPill data-testid={`feed-flagged-${event.id}`}>
                  <FlagSolidIcon />
                  Flagged
                </FlaggedPill>
              )}
            </DescriptionRow>
            <MetaRow>
              {/* GRAVY-10808 — `elapsedLabel`, the SAME rule DashboardV2's
                  activity rows use (Product's table, GRAVY-10358 §C1): "Just
                  now", "5 min ago", "3 hr ago", "1 day ago", then the date.

                  It replaces a local `shortDate` that only ever reached day
                  granularity, so a flag that fired six minutes ago read
                  "Today" where the AC asks for relative timestamps "when
                  recent ('Just now'), like the rest of the feed" — and the
                  rest of the feed was the part already doing it. One shared
                  formatter, not two that agree by coincidence.

                  `title` keeps the exact time reachable for audits: a relative
                  label reads better and settles nothing, so the full moment
                  (zone named) stays one hover away on every row. */}
              <span title={exactTime(event.occurred_at)}>
                {elapsedLabel(event.occurred_at)}
              </span>
              <MetaDot />
              {/* Humanized, not the raw enum: this rendered literally as
                  "Today • score_initialized" beside the already-readable
                  description. The identifier stays in `title` so it's still
                  greppable when someone is matching UI to a ledger row. */}
              <span title={event.event_type}>
                {humanizeEventType(event.event_type)}
              </span>
            </MetaRow>
          </Entry>
        ))}
      </List>
      {!isEmpty && errorNote}
      {!withHeader && viewMore}
    </Wrapper>
  );
};

export default ActivityFeed;
